import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query'

import { useRecord } from '@/shared/hooks/useRecord'
import { useRecords } from '@/shared/hooks/useRecords'
import { api } from '@/shared/lib/api'

import type { EventPost } from './types'

export const useFeedConfig = (eventSlug: string) =>
  useRecord({
    r: `/user/digital_products/${eventSlug}/event_feed_config`,
    forceIdPresence: false,
    options: { staleTime: 60_000 }
  })

export const useFeedPosts = (
  eventSlug: string,
  { perPage }: { perPage?: number } = {}
) =>
  useRecords({
    r: `/user/digital_products/${eventSlug}/event_posts`,
    params: perPage ? { per_page: perPage } : undefined,
    options: { refetchInterval: 30_000 }
  })

/**
 * Paginação real (scroll infinito) para o modo feed exclusivo — `useFeedPosts`
 * é fetch de página única, suficiente para o carrossel do hub mas não para
 * `FeedExperience`. Não reusa `useRecordsInfinite`/`ResourceRecordsProvider`:
 * `useRecordsInfinite` é hoje só um alias de `useRecords`
 * (`shared/hooks/useRecordsInfinite.js`), sem acumulação real de páginas —
 * corrigi-lo afetaria outras 5 features fora do escopo desta. Construído
 * direto sobre `useInfiniteQuery`.
 *
 * `getNextPageParam` compara `pagination.page` com `pagination.page_count`
 * — não `pagination.next_page`. Esse campo existe no payload
 * (`application_controller.rb#pagination_metadata`) mas é mapeado de
 * `pagy_metadata[:next_url]`, uma URL completa, não um número de página.
 */
export const useFeedPostsInfinite = (
  eventSlug: string,
  { perPage = 20 }: { perPage?: number } = {}
) => {
  const path = `/user/digital_products/${eventSlug}/event_posts`
  const query = useInfiniteQuery({
    queryKey: [path, perPage],
    queryFn: ({ pageParam = 1 }: { pageParam?: number }) =>
      api.get(`${path}?page=${pageParam}&per_page=${perPage}`).then(r => r.data),
    getNextPageParam: (last: { pagination?: { page?: number; page_count?: number } }) => {
      const { page, page_count: pageCount } = last.pagination ?? {}
      return typeof page === 'number' && typeof pageCount === 'number' && page < pageCount
        ? page + 1
        : undefined
    }
  })

  const pages = (query.data?.pages ?? []) as { records: EventPost[] }[]

  return {
    posts: pages.flatMap(p => p.records),
    isLoading: query.isLoading,
    isError: query.isError,
    isEmpty: query.isSuccess && (pages[0]?.records.length ?? 0) === 0,
    hasMore: !!query.hasNextPage,
    isFetchingMore: query.isFetchingNextPage,
    loadMore: query.fetchNextPage
  }
}

export const usePostComments = (eventSlug: string, postId: number | null) =>
  useRecords({
    r: postId
      ? `/user/digital_products/${eventSlug}/event_posts/${postId}/comments`
      : '',
    shouldFetch: !!eventSlug && !!postId
  })

export const useFeedQueryClient = () => useQueryClient()
